import { Address, erc721Abi, type Chain } from 'viem'
import { shibarium, dogechain, base, mainnet } from 'viem/chains'
import { getCollectionByAddress, type NFTCollection } from '@/constants/marketplace/collections'
import { type ChainType } from '@/constants/chain-specific'
import { getImageWithFallback } from '@/util/placeholderImage'
import { createRateLimitedClient } from './viem-batching'
import { ipfsGateways } from '../config'

// Define metadata structure
export interface NFTMetadata {
    name?: string
    description?: string
    image?: string
    external_url?: string
    attributes?: Array<{
        trait_type: string
        value: string | number
        display_type?: string
    }>
    animation_url?: string
    background_color?: string
    youtube_url?: string
    _lazyLoaded?: boolean // Flag for pattern-extrapolated metadata that should use lazy loading
    _debug?: {
        error?: string
        contractAddress?: string
        tokenId?: string
        chainId?: number
        timestamp?: number
        [key: string]: any
    }
    [key: string]: any
}

// Custom error class for metadata-related errors
export class MetadataError extends Error {
    constructor(
        message: string,
        public readonly code: 'TOKEN_URI_FAILED' | 'FETCH_FAILED' | 'PARSE_FAILED' | 'COLLECTION_NOT_FOUND' | 'RPC_CONNECTION_FAILED',
        public readonly tokenId: string,
        public readonly contractAddress: string
    ) {
        super(message)
        this.name = 'MetadataError'
    }
}

// Cache for metadata to avoid redundant fetches
const metadataCache = new Map<string, { data: NFTMetadata; timestamp: number }>()
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

// Enhanced pattern caching to avoid re-analyzing the same collection
const patternCache = new Map<string, {
    tokenURIPattern?: TokenURIPattern | null;
    imagePattern?: ImagePattern | null;
    timestamp: number
}>()
const PATTERN_CACHE_DURATION = 30 * 60 * 1000 // 30 minutes


// Add detailed error tracking
const errorStats = new Map<string, { count: number; lastError: string; timestamp: number }>()

// Enhanced rate limiting for gateway requests with exponential backoff
const gatewayRequestTracker = new Map<string, {
    requests: number;
    lastReset: number;
    failureCount: number;
    lastFailure: number;
    backoffDelay: number;
}>()
const GATEWAY_RATE_LIMIT = 5// Reduced from 5 to be extremely conservative
const RATE_LIMIT_WINDOW = 60 * 1000 // 2 minutes (was 1 minute)
const MAX_BACKOFF_DELAY = 1000 // 60 seconds max backoff (was 30s)

// Map chainId to viem chain definition
const chainMap: Record<number, Chain> = {
    109: shibarium,
    2000: dogechain,
    8453: base,
    1: mainnet
}

// Add URL validation helpers
function ensureTrailingSlash(url: string): string {
    return url.endsWith('/') ? url : `${url}/`;
}

function validateAndCleanBaseUrl(url: string): string {
    // Remove any double slashes (except after protocol)
    let cleaned = url.replace(/([^:])\/+/g, '$1/');
    // Ensure trailing slash
    cleaned = ensureTrailingSlash(cleaned);
    return cleaned;
}

// Update sequential patterns with more precise regex
const sequentialPatterns = [
    // Basic sequential with extension - matches baseUrl/123.json or baseUrl/folder/123.json
    {
        regex: /^(.+?)\/(\d+)\.(\w+)$/,
        hasExtension: true,
    },
    // Basic sequential without extension - matches baseUrl/123 or baseUrl/folder/123
    {
        regex: /^(.+?)\/(\d+)$/,
        hasExtension: false,
    },
    // IPFS sequential with extension - matches ipfs://hash/123.json
    {
        regex: /^ipfs:\/\/([a-zA-Z0-9]+)\/(\d+)\.(\w+)$/,
        hasExtension: true,
    },
    // IPFS sequential without extension - matches ipfs://hash/123
    {
        regex: /^ipfs:\/\/([a-zA-Z0-9]+)\/(\d+)$/,
        hasExtension: false,
    }
] as const;

// Add at the top of the file with other imports
const inFlightRequests = new Map<string, Promise<NFTMetadata>>();

/**
 * Enhanced gateway rate limiting with exponential backoff
 */
function isGatewayRateLimited(gatewayUrl: string): boolean {
    const now = Date.now()
    const tracker = gatewayRequestTracker.get(gatewayUrl)

    if (!tracker) {
        gatewayRequestTracker.set(gatewayUrl, {
            requests: 25,
            lastReset: now,
            failureCount: 0,
            lastFailure: 0,
            backoffDelay: 150
        })
        return false
    }

    // Check if we're in a backoff period due to recent failures
    if (tracker.failureCount > 0 && now - tracker.lastFailure < tracker.backoffDelay) {
        console.warn(`🚫 Gateway in backoff period: ${gatewayUrl} (${Math.ceil((tracker.backoffDelay - (now - tracker.lastFailure)) / 1000)}s remaining)`)
        return true
    }

    // Reset counter if window expired
    if (now - tracker.lastReset > RATE_LIMIT_WINDOW) {
        tracker.requests = 1
        tracker.lastReset = now
        // Reduce backoff on successful window completion
        tracker.backoffDelay = Math.max(1000, tracker.backoffDelay * 0.5)
        return false
    }

    // Check if limit exceeded
    if (tracker.requests >= GATEWAY_RATE_LIMIT) {
        console.warn(`🚫 Gateway rate limited: ${gatewayUrl}`)
        tracker.failureCount++
        tracker.lastFailure = now
        tracker.backoffDelay = Math.min(MAX_BACKOFF_DELAY, tracker.backoffDelay * 2)
        return true
    }

    tracker.requests++
    return false
}

/**
 * Track gateway failures for better backoff management
 */
function trackGatewayFailure(gatewayUrl: string, error: string) {
    const tracker = gatewayRequestTracker.get(gatewayUrl)
    if (tracker) {
        tracker.failureCount++
        tracker.lastFailure = Date.now()
        tracker.backoffDelay = Math.min(MAX_BACKOFF_DELAY, tracker.backoffDelay * 1.5)
        console.warn(`📉 Gateway failure tracked: ${gatewayUrl} (failures: ${tracker.failureCount}, backoff: ${tracker.backoffDelay}ms)`)
    }
}

/**
 * Add progressive delay with exponential backoff based on gateway index and failures
 */
async function addGatewayDelay(gatewayIndex: number, gatewayUrl?: string) {
    let delay = gatewayIndex * 1000 // Increased base delay: 0ms, 1s, 2s, 3s, etc.

    // Add additional delay if gateway has recent failures
    if (gatewayUrl) {
        const tracker = gatewayRequestTracker.get(gatewayUrl)
        if (tracker && tracker.failureCount > 0) {
            delay += Math.min(10000, tracker.failureCount * 2000) // Up to 10s additional delay with higher multiplier
        }
    }

    if (delay > 0) {
        console.log(`⏳ Adding ${delay}ms delay before gateway ${gatewayIndex + 1}...`)
        await new Promise(resolve => setTimeout(resolve, delay))
    }
}

/**
 * Get cached pattern for a collection
 */
function getCachedPattern(contractAddress: Address, chainId: ChainType): {
    tokenURIPattern?: TokenURIPattern | null;
    imagePattern?: ImagePattern | null;
} | null {
    const cacheKey = `${chainId}-${contractAddress}`
    const cached = patternCache.get(cacheKey)

    if (cached && Date.now() - cached.timestamp < PATTERN_CACHE_DURATION) {
        console.log(`📋 Using cached patterns for ${contractAddress}`)
        return {
            tokenURIPattern: cached.tokenURIPattern,
            imagePattern: cached.imagePattern
        }
    }

    return null
}

/**
 * Cache pattern for a collection
 */
function cachePattern(
    contractAddress: Address,
    chainId: ChainType,
    tokenURIPattern?: TokenURIPattern | null,
    imagePattern?: ImagePattern | null
) {
    const cacheKey = `${chainId}-${contractAddress}`
    patternCache.set(cacheKey, {
        tokenURIPattern,
        imagePattern,
        timestamp: Date.now()
    })
    console.log(`💾 Cached patterns for ${contractAddress}`)
}

/**
 * Track and log NFT metadata loading errors for debugging
 */
function trackMetadataError(contractAddress: Address, tokenId: string, error: string, context: string) {
    const key = `${contractAddress}-${tokenId}`
    const existing = errorStats.get(key) || { count: 0, lastError: '', timestamp: 0 }

    errorStats.set(key, {
        count: existing.count + 1,
        lastError: error,
        timestamp: Date.now()
    })

    console.error(`❌ NFT Metadata Error [${context}] ${contractAddress}:${tokenId}:`, {
        error,
        attempts: existing.count + 1,
        context
    })
}

/**
 * Enhanced IPFS URL conversion with more gateways and better error handling
 */
function convertIpfsUrl(url: string, gatewayIndex = 0): string {
    if (!url) return url

    // Already a standard HTTP URL (not IPFS)
    if (url.startsWith('http') && !url.includes('/ipfs/')) {
        return url
    }

    let ipfsPath = ''

    // Extract IPFS hash and path from various formats
    if (url.startsWith('ipfs://')) {
        ipfsPath = url.replace('ipfs://', '')
    } else if (url.startsWith('ipfs/')) {
        ipfsPath = url.replace('ipfs/', '')
    } else if (url.includes('/ipfs/')) {
        // Extract hash and path from existing gateway URL
        const ipfsIndex = url.indexOf('/ipfs/')
        ipfsPath = url.substring(ipfsIndex + 6) // +6 for '/ipfs/'
    } else if (url.match(/^Qm[1-9A-HJ-NP-Za-km-z]{44}$/)) {
        // Raw IPFS hash (Qm format) - no additional path
        ipfsPath = url
    } else if (url.match(/^[a-z2-7]{59}$/)) {
        // Raw IPFS hash (newer CID format) - no additional path
        ipfsPath = url
    } else if (url.match(/^bafy[a-z2-7]{55}$/)) {
        // IPFS CIDv1 format
        ipfsPath = url
    } else {
        // Not an IPFS URL, return as is
        console.warn('⚠️ Unknown URL format, returning as-is:', url)
        return url
    }

    // Extract just the hash part for validation, but preserve the full path
    const hashPart = ipfsPath.split('/')[0]

    // Validate hash format
    if (!hashPart || (!hashPart.startsWith('Qm') && !hashPart.match(/^[a-z2-7]{59}$/) && !hashPart.startsWith('bafy'))) {
        console.warn('⚠️ Invalid IPFS hash format:', hashPart, 'from URL:', url)
        return url // Return original if can't parse
    }


    const gateway = ipfsGateways[gatewayIndex] || ipfsGateways[0]
    const convertedUrl = `${gateway}${ipfsPath}` // Preserve full path including token ID and extension

    console.log(`🔄 Converting IPFS URL (gateway ${gatewayIndex + 1}/${ipfsGateways.length}): ${url} → ${convertedUrl}`)
    return convertedUrl
}

/**
 * Test if a URL is likely an IPFS URL
 */
function isIpfsUrl(url: string): boolean {
    if (!url) return false

    return url.startsWith('ipfs://') ||
        url.startsWith('ipfs/') ||
        url.includes('/ipfs/') ||
        url.match(/^Qm[1-9A-HJ-NP-Za-km-z]{44}$/) !== null ||
        url.match(/^[a-z2-7]{59}$/) !== null
}

/**
 * Enhanced metadata fetching with comprehensive error handling and multiple fallback strategies
 */
async function fetchMetadataWithRetry(url: string, maxRetries = 8): Promise<NFTMetadata> {
    let lastError: Error | null = null
    const originalUrl = url

    console.log(`📡 Starting metadata fetch for URL: ${url}`)

    // If it's not an IPFS URL, try direct fetch with retries
    if (!isIpfsUrl(url)) {
        for (let attempt = 0; attempt < Math.min(maxRetries, 3); attempt++) {
            try {
                console.log(`📡 Direct HTTP fetch attempt ${attempt + 1}:`, url)

                const controller = new AbortController()
                const timeoutId = setTimeout(() => controller.abort(), 20000) // Increased timeout to 20 seconds

                const response = await fetch(url, {
                    headers: {
                        'Accept': 'application/json',
                        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
                    },
                    signal: controller.signal
                })

                clearTimeout(timeoutId)

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
                }

                // Removed aggressive content length check - let browser handle response size validation

                const metadata = await response.json()
                console.log('✅ Successfully fetched metadata from HTTP URL')
                return metadata

            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : String(error)
                console.warn(`❌ HTTP fetch attempt ${attempt + 1} failed:`, errorMsg)
                lastError = error instanceof Error ? error : new Error(errorMsg)

                if (attempt < Math.min(maxRetries, 3) - 1) {
                    // Simplified backoff - just 1s delay between attempts
                    const delay = 1000
                    console.log(`⏳ Retry delay: ${delay}ms`)
                    await new Promise(resolve => setTimeout(resolve, delay))
                }
            }
        }
        throw lastError || new Error('All HTTP fetch attempts failed')
    }

    for (let gatewayIndex = 0; gatewayIndex < ipfsGateways.length; gatewayIndex++) {
        const fetchUrl = convertIpfsUrl(originalUrl, gatewayIndex)

        try {
            console.log(`📡 IPFS fetch via gateway ${gatewayIndex + 1}/${ipfsGateways.length}:`, fetchUrl)

            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 20000) // Increased timeout

            const response = await fetch(fetchUrl, {
                headers: {
                    'Accept': 'application/json',
                    'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
                },
                signal: controller.signal
            })

            clearTimeout(timeoutId)

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            const text = await response.text()
            if (!text.trim()) {
                throw new Error('Empty response body')
            }

            let metadata
            try {
                metadata = JSON.parse(text)
            } catch (parseError) {
                console.error(`❌ JSON parse error for gateway ${gatewayIndex + 1}:`, parseError)
                throw new Error(`Invalid JSON response: ${parseError}`)
            }

            console.log(`✅ Successfully fetched metadata via IPFS gateway ${gatewayIndex + 1}`)
            return metadata

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error)
            console.warn(`❌ IPFS gateway ${gatewayIndex + 1} failed:`, errorMsg)
            lastError = error instanceof Error ? error : new Error(errorMsg)
        }
    }

    console.error(`❌ All ${ipfsGateways.length} IPFS gateways failed for URL:`, originalUrl)
    throw lastError || new Error('All IPFS gateway attempts failed')
}

/**
 * Get token URI from contract using viem
 */
async function getTokenUriFromContract(
    contractAddress: Address,
    tokenId: bigint,
    chainId: ChainType,
    isERC1155 = false
): Promise<string> {
    try {
        console.log(`🔗 Getting tokenURI from contract for token ${tokenId.toString()} on chain ${chainId}`);

        // Debug logging for SIYC contract
        if (contractAddress.toLowerCase() === '0xb33012feb12d590f4ee9b0d9999e95c30303a209') {
            console.log(`🔍 SIYC DEBUG: getTokenUriFromContract called with:`, {
                contractAddress,
                tokenId: tokenId.toString(),
                chainId,
                isERC1155,
                stackTrace: new Error().stack?.split('\n').slice(1, 5)
            });
        }

        const chain = chainMap[chainId];
        if (!chain) {
            throw new Error(`Unsupported chain ID: ${chainId}. No chain info found.`);
        }

        // Create rate-limited client with proper configuration
        const publicClient = createRateLimitedClient(chain, chainId, {
            bypassForNFTMetadata: false // Don't bypass rate limiting for contract calls
        });

        if (!publicClient) {
            throw new Error(`Failed to create client for chain ${chainId}`);
        }

        // Test connection
        try {
            await publicClient.getBlockNumber()
            console.log(`✅ Successfully connected to an RPC for chain ${chainId}`)
        } catch (error) {
            let errorDetails = ''
            if (error instanceof Error) {
                errorDetails = `Name: ${error.name}, Message: ${error.message}`
                if (error.cause) {
                    errorDetails += `, Cause: ${String(error.cause)}`
                }
            } else {
                errorDetails = String(error)
            }
            console.error(`❌ Failed to connect to RPC for chain ${chainId}:`, errorDetails)
            throw new MetadataError(
                `Failed to connect to RPC for chain ${chainId}: ${errorDetails}`,
                'RPC_CONNECTION_FAILED',
                tokenId.toString(),
                contractAddress
            )
        }



        if (isERC1155) {
            // For ERC1155, use uri() function
            const uri = await publicClient.readContract({
                address: contractAddress,
                abi: [
                    {
                        inputs: [{ name: '_id', type: 'uint256' }],
                        name: 'uri',
                        outputs: [{ name: '', type: 'string' }],
                        stateMutability: 'view',
                        type: 'function'
                    }
                ],
                functionName: 'uri',
                args: [tokenId]
            })

            return uri as string
        } else {
            // For ERC721, use tokenURI() function
            const tokenURI = await publicClient.readContract({
                address: contractAddress,
                abi: erc721Abi,
                functionName: 'tokenURI',
                args: [tokenId]
            })

            return tokenURI as string
        }
    } catch (error: any) {
        let errorDetails = '';
        if (error instanceof Error) {
            errorDetails = `Name: ${error.name}, Message: ${error.message}`;
            if (error.cause) {
                errorDetails += `, Cause: ${String(error.cause)}`;
            }
            if (typeof (error as any).status === 'number') { // Common for HTTP errors wrapped by Viem
                errorDetails += `, HTTP Status: ${(error as any).status}`;
            }
            // Add more specific Viem error checks if needed, e.g. error.code for RpcError
            if (typeof (error as any).code === 'number') {
                errorDetails += `, RPC Error Code: ${(error as any).code}`;
            }
        } else {
            errorDetails = String(error);
        }
        console.error(`❌ Failed to get tokenURI from contract ${contractAddress} for token ${tokenId} on chain ${chainId}. Details: ${errorDetails}`, error);

        if (error instanceof MetadataError) { // If it's already our custom error, rethrow
            throw error;
        }

        const errorMessage = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
        if (errorMessage.includes('nonexistent token') || errorMessage.includes('token owner query for nonexistent token') || errorMessage.includes('invalid token id') || (typeof (error as any).code === 'number' && (error as any).code === -32000) /* common for execution reverted */) {
            throw new MetadataError(
                `Token ${tokenId.toString()} does not exist or query reverted on contract ${contractAddress} (chain ${chainId}). Error: ${errorDetails}`,
                'TOKEN_URI_FAILED',
                tokenId.toString(),
                contractAddress
            )
        }

        // For other errors, wrap them in MetadataError, including the detailed message
        throw new MetadataError(
            `Failed to get tokenURI for ${tokenId.toString()} on ${contractAddress} (chain ${chainId}): ${errorDetails}`,
            'TOKEN_URI_FAILED',
            tokenId.toString(),
            contractAddress
        )
    }
}

/**
 * Process ERC1155 token URI by substituting {id} placeholder
 */
function processERC1155TokenUri(uri: string, tokenId: bigint): string {
    if (!uri.includes('{id}')) return uri

    // Convert tokenId to lowercase hex string padded to 64 characters
    const hexId = tokenId.toString(16).toLowerCase().padStart(64, '0')
    return uri.replace(/{id}/g, hexId)
}

/**
 * Get metadata using collection's predefined metadata source
 * Returns null if no metadata source is configured
 */
async function getMetadataFromCollectionSource(
    collection: NFTCollection,
    tokenId: bigint
): Promise<NFTMetadata | null> {
    const tokenIdStr = tokenId.toString()

    // Check if collection has custom getTokenMetadata function
    if (collection.getTokenMetadata) {
        console.log(`📋 Using collection's custom getTokenMetadata function`)
        return await collection.getTokenMetadata(tokenIdStr, collection.chainId)
    }

    // Use predefined metadataSource
    if (collection.metadataSource) {
        console.log(`📋 Using collection's predefined metadataSource`)

        const baseUrl = collection.metadataSource
            .replace('ipfs://', 'https://ipfs.io/ipfs/')
            .replace(/\/$/, '')

        // Handle metadataExt: string adds extension, false adds none, undefined defaults to .json
        let metadataUrl: string
        if (collection.metadataExt === false) {
            metadataUrl = `${baseUrl}/${tokenIdStr}`
        } else if (typeof collection.metadataExt === 'string') {
            metadataUrl = `${baseUrl}/${tokenIdStr}${collection.metadataExt}`
        } else {
            // Default to .json if metadataExt is undefined/null
            metadataUrl = `${baseUrl}/${tokenIdStr}.json`
        }

        // Handle ERC1155 {id} substitution if needed
        if (collection.type === 'ERC1155' && metadataUrl.includes('{id}')) {
            metadataUrl = processERC1155TokenUri(metadataUrl, tokenId)
        }

        console.log(`🔗 Fetching metadata from: ${metadataUrl}`)
        return await fetchMetadataWithRetry(metadataUrl)
    }

    // Return null instead of throwing - this is a normal case
    console.log('📋 No metadata source in collection config, will use tokenURI')
    return null
}

/**
 * Enhanced getNFTMetadata with better error tracking and recovery
 */
export async function getNFTMetadata(
    contractAddress: Address,
    tokenId: bigint,
    chainId: ChainType,
    options: {
        useCache?: boolean
        skipCollectionConfig?: boolean
        isERC1155?: boolean
    } = {}
): Promise<NFTMetadata> {
    const { useCache = true, skipCollectionConfig = false, isERC1155 = false } = options;
    const tokenIdStr = tokenId.toString();
    const cacheKey = `${chainId}-${contractAddress}-${tokenIdStr}`;

    // Check cache first
    if (useCache && metadataCache.has(cacheKey)) {
        const cached = metadataCache.get(cacheKey)!;
        if (Date.now() - cached.timestamp < CACHE_DURATION) {
            console.log(`💾 Cache hit for ${contractAddress}:${tokenIdStr}`);
            return cached.data;
        }
        console.log(`💾 Cache expired for ${contractAddress}:${tokenIdStr}`);
    }

    // Check if there's an in-flight request for this token
    const inFlightRequest = inFlightRequests.get(cacheKey);
    if (inFlightRequest) {
        console.log(`🔄 Reusing in-flight request for ${contractAddress}:${tokenIdStr}`);
        return inFlightRequest;
    }

    // Create new request
    const request = (async () => {
        try {
            console.log(`🎯 Getting NFT metadata for ${contractAddress}:${tokenIdStr} on chain ${chainId}`);

            let metadata: NFTMetadata | null = null;

            // Step 1: Try to use collection's predefined metadata source
            if (!skipCollectionConfig) {
                const collection = getCollectionByAddress(contractAddress, chainId);
                if (collection) {
                    console.log(`📚 Found collection config: ${collection.name}`);
                    try {
                        metadata = await getMetadataFromCollectionSource(collection, tokenId);
                        if (metadata) {
                            console.log(`✅ Got metadata from collection source`);
                        }
                    } catch (error) {
                        console.warn(`⚠️ Collection source failed:`, error);
                        trackMetadataError(contractAddress, tokenIdStr, String(error), 'collection-source');
                    }
                }
            }

            // Step 2: If no collection config or it failed, get URI from contract
            if (!metadata) {
                let tokenURI: string;
                try {
                    tokenURI = await getTokenUriFromContract(contractAddress, tokenId, chainId, isERC1155);
                    if (!tokenURI) {
                        throw new MetadataError(
                            'Token URI is empty',
                            'TOKEN_URI_FAILED',
                            tokenIdStr,
                            contractAddress
                        );
                    }
                    console.log(`🔗 Got tokenURI from contract: ${tokenURI}`);
                } catch (error) {
                    trackMetadataError(contractAddress, tokenIdStr, String(error), 'contract-uri');
                    throw error;
                }

                // Process ERC1155 URI if needed
                let processedURI = isERC1155 ? processERC1155TokenUri(tokenURI, tokenId) : tokenURI;

                // Handle base64-encoded onchain metadata
                if (processedURI.startsWith('data:application/json;base64,')) {
                    try {
                        const base64Data = processedURI.replace('data:application/json;base64,', '');
                        const decodedJson = atob(base64Data);
                        metadata = JSON.parse(decodedJson);
                        console.log(`✅ Decoded base64 onchain metadata`);
                    } catch (error) {
                        trackMetadataError(contractAddress, tokenIdStr, String(error), 'base64-decode');
                        console.error(`❌ Failed to decode base64 metadata:`, error);
                        throw error;
                    }
                } else {
                    // Convert IPFS URI to HTTP before fetching
                    if (isIpfsUrl(processedURI)) {
                        const originalURI = processedURI;
                        processedURI = convertIpfsUrl(processedURI, 0);
                        console.log(`🔄 Converted tokenURI from IPFS: ${originalURI} → ${processedURI}`);
                    }

                    // Fetch metadata from URI
                    try {
                        metadata = await fetchMetadataWithRetry(processedURI);
                        console.log(`✅ Got metadata from URI: ${processedURI}`);
                    } catch (error) {
                        trackMetadataError(contractAddress, tokenIdStr, String(error), 'metadata-fetch');
                        console.error(`❌ Failed to fetch metadata from ${processedURI}:`, error);
                        throw error;
                    }
                }
            }

            if (!metadata) {
                throw new MetadataError(
                    'Failed to get metadata from any source',
                    'FETCH_FAILED',
                    tokenIdStr,
                    contractAddress
                );
            }

            // Step 3: Process and enhance metadata
            const processedMetadata: NFTMetadata = {
                ...metadata,
                image: metadata.image
                    ? (metadata.image.startsWith('data:image/')
                        ? metadata.image // Keep base64 images as-is
                        : (isIpfsUrl(metadata.image) ? convertIpfsUrl(metadata.image) : metadata.image)
                      )
                    : getImageWithFallback(null),
                name: metadata.name || `Token #${tokenIdStr}`,
                animation_url: metadata.animation_url && isIpfsUrl(metadata.animation_url)
                    ? convertIpfsUrl(metadata.animation_url)
                    : metadata.animation_url,
                external_url: metadata.external_url && isIpfsUrl(metadata.external_url)
                    ? convertIpfsUrl(metadata.external_url)
                    : metadata.external_url,
                attributes: Array.isArray(metadata.attributes) ? metadata.attributes : []
            };

            // Validate the processed metadata
            if (!processedMetadata.name && !processedMetadata.image) {
                console.warn(`⚠️ Metadata appears empty for ${contractAddress}:${tokenIdStr}`);
            }

            // Cache the result
            if (useCache) {
                metadataCache.set(cacheKey, {
                    data: processedMetadata,
                    timestamp: Date.now()
                });
            }

            console.log(`✅ Successfully retrieved and processed metadata for ${contractAddress}:${tokenIdStr}`);
            return processedMetadata;
        } catch (error) {
            console.error(`❌ Failed to get NFT metadata for ${contractAddress}:${tokenIdStr}:`, error);
            throw error;
        } finally {
            // Clean up in-flight request
            inFlightRequests.delete(cacheKey);
        }
    })();

    // Store the request
    inFlightRequests.set(cacheKey, request);

    return request;
}

/**
 * Get error statistics for debugging
 */
export function getMetadataErrorStats() {
    const stats = Array.from(errorStats.entries()).map(([key, data]) => ({
        token: key,
        ...data,
        lastErrorTime: new Date(data.timestamp).toISOString()
    }))

    return {
        totalErrors: stats.length,
        errors: stats.sort((a, b) => b.count - a.count).slice(0, 20) // Top 20 most problematic tokens
    }
}

/**
 * Batch fetch multiple NFT metadata
 * Useful for loading collection pages efficiently
 */
export async function batchGetNFTMetadata(
    requests: Array<{
        contractAddress: Address
        tokenId: bigint
        chainId: ChainType
        isERC1155?: boolean
    }>,
    options: {
        useCache?: boolean
        batchSize?: number
        skipCollectionConfig?: boolean
    } = {}
): Promise<Array<{ tokenId: bigint; metadata: NFTMetadata; error?: MetadataError }>> {
    const { batchSize = 10, ...fetchOptions } = options
    const results: Array<{ tokenId: bigint; metadata: NFTMetadata; error?: MetadataError }> = []

    // Process in batches to avoid overwhelming servers
    for (let i = 0; i < requests.length; i += batchSize) {
        const batch = requests.slice(i, i + batchSize)

        const batchPromises = batch.map(async ({ contractAddress, tokenId, chainId, isERC1155 }) => {
            try {
                const metadata = await getNFTMetadata(contractAddress, tokenId, chainId, {
                    ...fetchOptions,
                    isERC1155
                })
                return { tokenId, metadata }
            } catch (error) {
                console.warn(`⚠️ Failed to fetch metadata for token ${tokenId.toString()}:`, error)

                const metadataError = error instanceof MetadataError
                    ? error
                    : new MetadataError(
                        error instanceof Error ? error.message : String(error),
                        'FETCH_FAILED',
                        tokenId.toString(),
                        contractAddress
                    )

                // Create fallback metadata for non-existent or failed tokens
                const fallbackMetadata: NFTMetadata = {
                    name: `Token #${tokenId.toString()}`,
                    description: error instanceof MetadataError && error.message.includes('does not exist')
                        ? 'This token does not exist'
                        : 'Failed to load metadata',
                    image: getImageWithFallback(null),
                    attributes: []
                }

                return {
                    tokenId,
                    metadata: fallbackMetadata,
                    error: metadataError
                }
            }
        })

        const batchResults = await Promise.all(batchPromises)
        results.push(...batchResults)

        // Reduced delay between batches since rate limiting is handled elsewhere
        if (i + batchSize < requests.length) {
            await new Promise(resolve => setTimeout(resolve, 20))
        }
    }

    return results
}

/**
 * Clear metadata cache (useful for testing or forcing refresh)
 */
export function clearMetadataCache(contractAddress?: Address, tokenId?: bigint, chainId?: ChainType) {
    if (contractAddress && tokenId && chainId) {
        const cacheKey = `${chainId}-${contractAddress}-${tokenId.toString()}`
        metadataCache.delete(cacheKey)
    } else {
        metadataCache.clear()
    }
}

/**
 * Get cache statistics (useful for debugging)
 */
export function getMetadataCacheStats() {
    const now = Date.now()
    let validEntries = 0
    let expiredEntries = 0

    metadataCache.forEach(({ timestamp }) => {
        if (now - timestamp < CACHE_DURATION) {
            validEntries++
        } else {
            expiredEntries++
        }
    })

    return {
        totalEntries: metadataCache.size,
        validEntries,
        expiredEntries,
        cacheHitRate: validEntries / (validEntries + expiredEntries) || 0
    }
}

/**
 * Optimized batch fetch that uses readContracts for efficient tokenURI batching
 * and implements pattern recognition to avoid unnecessary contract calls
 */
export async function batchGetNFTMetadataOptimized(
    requests: Array<{
        contractAddress: Address
        tokenId: bigint
        chainId: ChainType
        isERC1155?: boolean
    }>,
    options: {
        useCache?: boolean
        batchSize?: number
        skipCollectionConfig?: boolean
        enablePatternRecognition?: boolean
    } = {}
): Promise<Array<{ tokenId: bigint; metadata: NFTMetadata; error?: MetadataError }>> {
    const {
        batchSize = 10, // Reduced batch size to prevent gateway overload
        enablePatternRecognition = true,
        ...fetchOptions
    } = options

    if (requests.length === 0) {
        return []
    }

    // Group requests by contract and chain for efficient batching
    const groupedRequests = new Map<string, typeof requests>()
    for (const request of requests) {
        const key = `${request.chainId}-${request.contractAddress}-${request.isERC1155 ? 'erc1155' : 'erc721'}`
        if (!groupedRequests.has(key)) {
            groupedRequests.set(key, [])
        }
        groupedRequests.get(key)!.push(request)
    }

    const allResults: Array<{ tokenId: bigint; metadata: NFTMetadata; error?: MetadataError }> = []

    // Process each group separately
    for (const [groupKey, groupRequests] of groupedRequests) {
        console.log(`🎯 Processing ${groupRequests.length} tokens for group: ${groupKey}`)

        try {
            const groupResults = await processTokenGroup(groupRequests, {
                ...fetchOptions,
                batchSize,
                enablePatternRecognition: false
            })
            allResults.push(...groupResults)
        } catch (error) {
            console.error(`❌ Failed to process group ${groupKey}:`, error)

            // Create fallback results for the entire group
            for (const request of groupRequests) {
                allResults.push({
                    tokenId: request.tokenId,
                    metadata: {
                        name: `Token #${request.tokenId.toString()}`,
                        description: 'Failed to load metadata (group error)',
                        image: getImageWithFallback(null),
                        attributes: []
                    },
                    error: new MetadataError(
                        `Group processing failed: ${error instanceof Error ? error.message : String(error)}`,
                        'FETCH_FAILED',
                        request.tokenId.toString(),
                        request.contractAddress
                    )
                })
            }
        }
    }

    return allResults
}

/**
 * Process a group of tokens from the same contract efficiently
 */
async function processTokenGroup(
    requests: Array<{
        contractAddress: Address
        tokenId: bigint
        chainId: ChainType
        isERC1155?: boolean
    }>,
    options: {
        useCache?: boolean
        batchSize?: number
        skipCollectionConfig?: boolean
        enablePatternRecognition?: boolean
    }
): Promise<Array<{ tokenId: bigint; metadata: NFTMetadata; error?: MetadataError }>> {
    if (requests.length === 0) return [];

    const { contractAddress, chainId, isERC1155 } = requests[0];
    const {
        batchSize = 100,
        enablePatternRecognition = true,
        skipCollectionConfig = false,
        useCache = true
    } = options;

    const results: Array<{ tokenId: bigint; metadata: NFTMetadata; error?: MetadataError }> = [];

    try {
        // Check if collection has predefined metadata source


        // Check for cached patterns
        const cachedPatterns = getCachedPattern(contractAddress, chainId);
        let patternInfo: TokenURIPattern | null = cachedPatterns?.tokenURIPattern || null;
        let imagePattern: ImagePattern | null = cachedPatterns?.imagePattern || null;
        let sampleMetadata: NFTMetadata | null = null;

        // Only fetch tokenURIs if we don't have cached patterns
        let tokenURIs: Map<string, string> = new Map();

            try {
                // Batch fetch tokenURIs using readContracts
                tokenURIs = await batchFetchTokenURIs(requests, batchSize);

                if (tokenURIs.size === 0) {
                    throw new MetadataError(
                        'Failed to fetch any tokenURIs from contract',
                        'TOKEN_URI_FAILED',
                        requests[0].tokenId.toString(),
                        contractAddress
                    );
                }

            } catch (error) {
                console.error(`❌ Failed to fetch tokenURIs or analyze patterns:`, error);
                // Don't throw here - continue with individual fetches
            }


        // Process each token
        for (const request of requests) {
            const tokenIdStr = request.tokenId.toString();
            const cacheKey = `${chainId}-${contractAddress}-${tokenIdStr}`;

            try {
                // Check cache first
                if (useCache && metadataCache.has(cacheKey)) {
                    const cached = metadataCache.get(cacheKey)!;
                    if (Date.now() - cached.timestamp < CACHE_DURATION) {
                        results.push({ tokenId: request.tokenId, metadata: cached.data });
                        continue;
                    }
                }

                let metadata: NFTMetadata;

                    // Direct fetch using tokenURI
                    const tokenURI = tokenURIs.get(`${request.contractAddress}-${request.tokenId}`);
                    if (!tokenURI) {
                        throw new MetadataError(
                            'No tokenURI found for token',
                            'TOKEN_URI_FAILED',
                            tokenIdStr,
                            contractAddress
                        );
                    }

                    let processedURI = isERC1155 ? processERC1155TokenUri(tokenURI, request.tokenId) : tokenURI;
                    if (isIpfsUrl(processedURI)) {
                        processedURI = convertIpfsUrl(processedURI, 0);
                    }

                    metadata = await fetchMetadataWithRetry(processedURI);


                // Process and enhance metadata
                const processedMetadata: NFTMetadata = {
                    ...metadata,
                    image: metadata.image
                        ? (metadata.image.startsWith('data:image/')
                            ? metadata.image // Keep base64 images as-is
                            : (isIpfsUrl(metadata.image) ? convertIpfsUrl(metadata.image) : metadata.image)
                          )
                        : getImageWithFallback(null),
                    name: metadata.name || `Token #${tokenIdStr}`,
                    animation_url: metadata.animation_url && isIpfsUrl(metadata.animation_url)
                        ? convertIpfsUrl(metadata.animation_url)
                        : metadata.animation_url,
                    external_url: metadata.external_url && isIpfsUrl(metadata.external_url)
                        ? convertIpfsUrl(metadata.external_url)
                        : metadata.external_url,
                    attributes: Array.isArray(metadata.attributes) ? metadata.attributes : []
                };

                // Cache the result
                if (useCache) {
                    metadataCache.set(cacheKey, {
                        data: processedMetadata,
                        timestamp: Date.now()
                    });
                }

                results.push({ tokenId: request.tokenId, metadata: processedMetadata });

            } catch (error) {
                console.warn(`⚠️ Error fetching metadata for token ${tokenIdStr}:`, error);

                // Try image pattern as last resort
                let fallbackImage = getImageWithFallback(null);
                if (imagePattern?.isReliable) {
                    try {
                        const fallbackMetadata = generateMetadataFromImagePattern(request.tokenId, imagePattern, sampleMetadata || undefined);
                        fallbackImage = fallbackMetadata.image || fallbackImage;
                    } catch (fallbackError) {
                        console.warn(`Image pattern fallback failed for ${tokenIdStr}`);
                    }
                }

                const fallbackMetadata: NFTMetadata = {
                    name: `Token #${tokenIdStr}`,
                    description: error instanceof MetadataError ? error.message : 'Failed to load metadata',
                    image: fallbackImage,
                    attributes: [],
                    _debug: {
                        error: error instanceof Error ? error.message : String(error),
                        contractAddress,
                        tokenId: tokenIdStr,
                        chainId,
                        timestamp: Date.now()
                    }
                };

                results.push({
                    tokenId: request.tokenId,
                    metadata: fallbackMetadata,
                    error: error instanceof MetadataError ? error : new MetadataError(
                        error instanceof Error ? error.message : String(error),
                        'FETCH_FAILED',
                        tokenIdStr,
                        contractAddress
                    )
                });
            }
        }

        return results;

    } catch (error) {
        console.error(`❌ Critical error processing token group:`, error);

        // Create fallback results for all tokens
        return requests.map(request => ({
            tokenId: request.tokenId,
            metadata: {
                name: `Token #${request.tokenId.toString()}`,
                description: 'Failed to load metadata (group error)',
                image: getImageWithFallback(null),
                attributes: [],
                _debug: {
                    error: error instanceof Error ? error.message : String(error),
                    contractAddress,
                    tokenId: request.tokenId.toString(),
                    chainId,
                    timestamp: Date.now()
                }
            },
            error: error instanceof MetadataError ? error : new MetadataError(
                error instanceof Error ? error.message : String(error),
                'FETCH_FAILED',
                request.tokenId.toString(),
                contractAddress
            )
        }));
    }
}

/**
 * Batch fetch tokenURIs using readContracts for efficiency
 */
async function batchFetchTokenURIs(
    requests: Array<{
        contractAddress: Address
        tokenId: bigint
        chainId: ChainType
        isERC1155?: boolean
    }>,
    batchSize: number
): Promise<Map<string, string>> {
    if (requests.length === 0) return new Map();

    const { contractAddress, chainId, isERC1155 } = requests[0];
    const tokenURIs = new Map<string, string>();

    const chain = chainMap[chainId];
    if (!chain) {
        throw new Error(`Unsupported chain ID: ${chainId}`);
    }

    // Create rate-limited client with proper configuration
    const publicClient = createRateLimitedClient(chain, chainId, {
        bypassForNFTMetadata: false // Don't bypass rate limiting for contract calls
    });

    if (!publicClient) {
        throw new Error(`Failed to create client for chain ${chainId}`);
    }

    // Test connection
    try {
        await publicClient.getBlockNumber()
        console.log(`✅ Successfully connected to an RPC for chain ${chainId} for batch fetching`)
    } catch (error) {
        let errorDetails = ''
        if (error instanceof Error) {
            errorDetails = `Name: ${error.name}, Message: ${error.message}`
            if (error.cause) {
                errorDetails += `, Cause: ${String(error.cause)}`
            }
        } else {
            errorDetails = String(error)
        }
        console.error(`❌ Failed to connect to RPC for chain ${chainId}:`, errorDetails)
        throw new MetadataError(
            `Failed to connect to RPC for chain ${chainId}: ${errorDetails}`,
            'RPC_CONNECTION_FAILED',
            requests[0].tokenId.toString(),
            requests[0].contractAddress
        )
    }

    // Prepare contract calls and ABIs
    const abi = isERC1155 ? [
        {
            inputs: [{ name: '_id', type: 'uint256' }],
            name: 'uri',
            outputs: [{ name: '', type: 'string' }],
            stateMutability: 'view',
            type: 'function'
        }
    ] as const : erc721Abi

    const functionName = isERC1155 ? 'uri' : 'tokenURI'

    // Process in batches
    for (let i = 0; i < requests.length; i += batchSize) {
        const batch = requests.slice(i, i + batchSize)
        try {
            const results = await Promise.all(
                batch.map(request =>
                    publicClient.readContract({
                        address: request.contractAddress,
                        abi,
                        functionName,
                        args: [request.tokenId]
                    })
                )
            )

            batch.forEach((request, index) => {
                const uri = results[index] as string
                tokenURIs.set(`${request.contractAddress}-${request.tokenId}`, uri)
            })
        } catch (error) {
            console.error(`Failed to fetch batch ${i}-${i + batchSize}:`, error)
            // Continue with next batch instead of failing completely
        }
    }

    return tokenURIs
}

interface TokenURIPattern {
    baseUrl: string;
    pattern: 'sequential' | 'ipfs_hash' | 'custom';
    isReliable: boolean;
    hasExtension: boolean;
    extension?: string;
    sampleSize: number;
    metadata?: {
        hasConsistentStructure: boolean;
        commonFields: string[];
        imagePattern?: string;
    };
    customFormatter?: (tokenId: string) => string;
    // Add new fields for better pattern detection
    isSequential?: boolean;
    sequentialInfo?: {
        baseUri: string;
        tokenIdPosition: 'end' | 'middle';
        prefix?: string;
        suffix?: string;
    };
}

interface ImagePattern {
    baseUrl: string;
    pattern: 'sequential' | 'ipfs_sequential' | 'ipfs_folder' | 'ipfs_prefixed' | 'http_prefixed' | 'custom';
    isReliable: boolean;
    hasExtension: boolean;
    extension?: string;
    sampleSize: number;
    metadata?: {
        namePattern?: string;
        attributesConsistent?: boolean;
    };
    // Add sequential pattern info
    sequentialInfo?: {
        baseUri: string;
        tokenIdPosition: 'end' | 'middle';
        prefix?: string;
        suffix?: string;
        isConfirmed: boolean;
    };
}

/**
 * Analyze tokenURI patterns to enable extrapolation
 */
/**
 * Enhanced tokenURI pattern analysis with proper extension detection
 */
function analyzeTokenURIPattern(tokenURIs: Map<string, string>): TokenURIPattern | null {
    const uris = Array.from(tokenURIs.entries())
    if (uris.length < 2) return null // Reduced minimum sample size to 2

    // Sort URIs by token ID for better sequential analysis
    const sortedUris = uris.sort(([idA], [idB]) => Number(idA) - Number(idB))

    // Take strategic samples - first, second, and if available, a middle token
    const sampleUris = [sortedUris[0]];
    if (sortedUris.length > 1) sampleUris.push(sortedUris[1]);
    if (sortedUris.length > 3) {
        const midIndex = Math.floor(sortedUris.length / 2);
        sampleUris.push(sortedUris[midIndex]);
    }

    console.log(`🔍 Analyzing tokenURI patterns from ${sampleUris.length} samples:`,
        sampleUris.map(([id, uri]) => `${id}: ${uri}`))

    // First, check for exact sequential patterns
    const sequentialPatterns = [
        // Basic sequential with extension
        {
            regex: /^(.+?)\/(\d+)\.(\w+)$/,
            hasExtension: true,
            name: 'Basic sequential with extension'
        },
        // Basic sequential without extension
        {
            regex: /^(.+?)\/(\d+)$/,
            hasExtension: false,
            name: 'Basic sequential without extension'
        },
        // IPFS sequential with extension
        {
            regex: /^ipfs:\/\/([a-zA-Z0-9]+)\/(\d+)\.(\w+)$/,
            hasExtension: true,
            name: 'IPFS sequential with extension'
        },
        // IPFS sequential without extension
        {
            regex: /^ipfs:\/\/([a-zA-Z0-9]+)\/(\d+)$/,
            hasExtension: false,
            name: 'IPFS sequential without extension'
        },
        // HTTP sequential with extension
        {
            regex: /^https?:\/\/[^\/]+\/(.+?)(\d+)\.(\w+)$/,
            hasExtension: true,
            name: 'HTTP sequential with extension'
        },
        // HTTP sequential without extension
        {
            regex: /^https?:\/\/[^\/]+\/(.+?)(\d+)$/,
            hasExtension: false,
            name: 'HTTP sequential without extension'
        }
    ];

    for (const pattern of sequentialPatterns) {
        let matches = 0;
        let baseUri = '';
        let extension = '';
        let isFullySequential = true;
        const matchDetails = [];

        for (const [tokenId, uri] of sampleUris) {
            const match = uri.match(pattern.regex);
            if (match) {
                const extractedBase = match[1];
                const extractedTokenId = match[2];
                const extractedExt = pattern.hasExtension ? match[3] : '';

                // Verify token ID position and value
                if (extractedTokenId === tokenId) {
                    matches++;
                    if (!baseUri) {
                        baseUri = extractedBase;
                        extension = extractedExt;
                    } else if (baseUri !== extractedBase ||
                              (pattern.hasExtension && extension !== extractedExt)) {
                        isFullySequential = false;
                        break;
                    }

                    matchDetails.push({
                        tokenId,
                        extractedTokenId,
                        baseUri: extractedBase,
                        extension: extractedExt,
                        matches: true
                    });
                }
            }
        }

        // If we have a perfect match across samples
        if (matches === sampleUris.length && isFullySequential) {
            console.log(`✅ Found perfect sequential pattern:`, {
                pattern: pattern.name,
                baseUri,
                extension: extension || 'none',
                matches
            });

            // Create pattern info
            return {
                baseUrl: baseUri,
                pattern: 'sequential',
                isReliable: true,
                hasExtension: pattern.hasExtension,
                extension,
                sampleSize: sampleUris.length,
                isSequential: true,
                sequentialInfo: {
                    baseUri,
                    tokenIdPosition: 'end',
                    suffix: pattern.hasExtension ? `.${extension}` : ''
                },
                metadata: {
                    hasConsistentStructure: true,
                    commonFields: ['name', 'image', 'attributes'],
                    imagePattern: undefined
                },
                customFormatter: (tokenId) => `${baseUri}${tokenId}${pattern.hasExtension ? `.${extension}` : ''}`
            };
        }
    }

    // If no sequential pattern found, fall back to IPFS pattern detection
    // ... existing IPFS pattern detection code ...

    return null;
}

/**
 * Simplified pattern detection with better URL validation
 */
async function analyzeImagePatterns(
    requests: Array<{ tokenId: bigint }>,
    tokenURIs: Map<string, string>,
    sampleSize: number = 2
): Promise<ImagePattern | null> {
    if (requests.length < 2) return null;

    // Get first two samples
    const analyzedMetadata = await getSampleMetadata(requests.slice(0, 2), tokenURIs);
    if (analyzedMetadata.length < 2) return null;

    // Sort by token ID
    const sortedImageData = analyzedMetadata
        .filter(sample => sample.metadata.image)
        .sort((a, b) => Number(a.tokenId) - Number(b.tokenId));

    if (sortedImageData.length < 2) return null;

    // Check each pattern
    for (const pattern of sequentialPatterns) {
        const [first, second] = sortedImageData;
        const match1 = first.metadata.image!.match(pattern.regex);
        const match2 = second.metadata.image!.match(pattern.regex);

        if (match1 && match2) {
            const baseUri1 = match1[1];
            const baseUri2 = match2[1];
            const ext1 = pattern.hasExtension ? match1[3] : '';
            const ext2 = pattern.hasExtension ? match2[3] : '';

            // If base URIs and extensions match, we have a pattern
            if (baseUri1 === baseUri2 && ext1 === ext2) {
                // Clean and validate the base URI
                const cleanBaseUri = validateAndCleanBaseUrl(baseUri1);

                return {
                    baseUrl: cleanBaseUri,
                    pattern: 'sequential' as const,
                    isReliable: true,
                    hasExtension: pattern.hasExtension,
                    extension: ext1,
                    sampleSize: 2,
                    metadata: {
                        namePattern: first.metadata.name,
                        attributesConsistent: true
                    },
                    sequentialInfo: {
                        baseUri: cleanBaseUri,
                        tokenIdPosition: 'end',
                        suffix: pattern.hasExtension ? `.${ext1}` : '',
                        isConfirmed: true
                    }
                };
            }
        }
    }

    return null;
}

/**
 * Construct tokenURI using pattern with proper URL validation
 */
function constructTokenUri(baseUri: string, tokenId: string, extension?: string): string {
    // Clean the base URI first
    const cleanBaseUri = validateAndCleanBaseUrl(baseUri);

    // For IPFS URLs, handle special case
    if (cleanBaseUri.startsWith('ipfs://')) {
        const ipfsPath = cleanBaseUri.replace('ipfs://', '').replace(/\/+$/, '');
        return `ipfs://${ipfsPath}/${tokenId}${extension ? `.${extension}` : ''}`;
    }

    // For HTTP URLs
    return `${cleanBaseUri}${tokenId}${extension ? `.${extension}` : ''}`;
}

/**
 * Simplified metadata generation with better URL construction
 */
function generateMetadataFromImagePattern(
    tokenId: bigint,
    imagePattern: ImagePattern,
    sampleMetadata?: NFTMetadata
): NFTMetadata {
    const tokenIdStr = tokenId.toString();

    // Generate image URL based on pattern
    const imageUrl = imagePattern.sequentialInfo
        ? constructTokenUri(
            imagePattern.sequentialInfo.baseUri,
            tokenIdStr,
            imagePattern.hasExtension ? imagePattern.extension : undefined
          )
        : constructTokenUri(
            imagePattern.baseUrl,
            tokenIdStr,
            imagePattern.hasExtension ? imagePattern.extension : undefined
          );

    // Generate metadata
    return {
        name: generateNameFromPattern(sampleMetadata?.name, tokenIdStr),
        description: sampleMetadata?.description || `Token ${tokenIdStr}`,
        image: imageUrl,
        attributes: imagePattern.metadata?.attributesConsistent
            ? (sampleMetadata?.attributes || [])
            : [],
        ...(sampleMetadata?.external_url && { external_url: sampleMetadata.external_url }),
        ...(sampleMetadata?.animation_url && { animation_url: sampleMetadata.animation_url }),
        ...(sampleMetadata?.background_color && { background_color: sampleMetadata.background_color }),
        _lazyLoaded: true
    };
}

/**
 * Simplified sample metadata fetching
 */
async function getSampleMetadata(
    requests: Array<{ tokenId: bigint }>,
    tokenURIs: Map<string, string>
): Promise<Array<{ tokenId: string; metadata: NFTMetadata }>> {
    const analyzedMetadata: Array<{ tokenId: string; metadata: NFTMetadata }> = [];

    for (const request of requests) {
        try {
            const tokenIdStr = request.tokenId.toString();
            const tokenURI = tokenURIs.get(tokenIdStr);
            if (!tokenURI) continue;

            let processedURI = tokenURI;
            if (isIpfsUrl(processedURI)) {
                processedURI = convertIpfsUrl(processedURI, 0);
            }

            const metadata = await fetchMetadataWithRetry(processedURI);
            if (metadata.image) {
                analyzedMetadata.push({ tokenId: tokenIdStr, metadata });
            }
        } catch (error) {
            console.warn(`Failed to fetch metadata for token ${request.tokenId}`);
        }
    }

    return analyzedMetadata;
}

/**
 * Generate name from pattern using sample metadata
 */
function generateNameFromPattern(sampleName?: string, tokenIdStr?: string): string {
    if (!sampleName || !tokenIdStr) {
        return `Token #${tokenIdStr || '?'}`
    }

    // Try to replace existing numbers with the new token ID
    const nameWithReplacedNumbers = sampleName.replace(/\d+/g, tokenIdStr)

    // If no numbers were found, try to append the token ID
    if (nameWithReplacedNumbers === sampleName) {
        // Look for common patterns and append token ID
        if (sampleName.toLowerCase().includes('token')) {
            return `${sampleName.replace(/token/i, `Token ${tokenIdStr}`)}`
        } else {
            return `${sampleName} ${tokenIdStr}`
        }
    }

    return nameWithReplacedNumbers
}

/**
 * Fetch metadata using recognized pattern with proper extension handling and validation
 * Now supports both HTTP fetching and pure extrapolation based on pattern metadata
 */
async function fetchMetadataUsingPattern(tokenId: bigint, pattern: TokenURIPattern): Promise<NFTMetadata> {
    const tokenIdStr = tokenId.toString();

    // If we have a confirmed sequential pattern, we can generate the URL directly
    if (pattern.isSequential && pattern.sequentialInfo) {
        const { baseUri, suffix = '' } = pattern.sequentialInfo;
        const metadataUrl = `${baseUri}${tokenIdStr}${suffix}`;

        // For sequential patterns, we can be more aggressive with extrapolation
        // If we've successfully loaded metadata for other tokens in this pattern,
        // we can assume this one will follow the same structure
        if (pattern.metadata?.hasConsistentStructure) {
            return {
                name: `Token #${tokenIdStr}`,
                description: `Token ${tokenIdStr}`,
                image: pattern.metadata.imagePattern ?
                    pattern.metadata.imagePattern.replace('{tokenId}', tokenIdStr) :
                    undefined,
                attributes: [],
                _lazyLoaded: true
            };
        }

        // If we're not sure about the metadata structure, fetch it
        try {
            const metadata = await fetchMetadataWithRetry(metadataUrl);
            return metadata;
        } catch (error) {
            console.error(`Failed to fetch metadata for sequential token ${tokenIdStr}:`, error);
            throw error;
        }
    }

    // Fall back to existing pattern handling for non-sequential patterns
    const extension = pattern.hasExtension && pattern.extension ? `.${pattern.extension}` : '';
    let metadataUrl: string;

    if (pattern.pattern === 'ipfs_hash') {
        metadataUrl = `${pattern.baseUrl}/${tokenIdStr}${extension}`;
    } else if (pattern.pattern === 'sequential') {
        metadataUrl = `${pattern.baseUrl}${tokenIdStr}${extension}`;
    } else {
        throw new Error(`Unsupported pattern for extrapolation: ${pattern.pattern}`);
    }

    // Validate the generated URL
    if (!metadataUrl || metadataUrl.length < 10) {
        throw new Error(`Invalid generated metadata URL: ${metadataUrl}`);
    }

    try {
        const metadata = await fetchMetadataWithRetry(metadataUrl);
        return metadata;
    } catch (error) {
        console.error(`Failed to fetch metadata using pattern for token ${tokenIdStr}:`, error);
        throw error;
    }
}